import { mobile } from '../../../constants';
import DynamicYield from '../DynamicYield';
import { dyApiConfig, dyChoose, getDefaultPageContext } from './ChooseRequest';

const MAX_LISTED_ITEMS = 300;
const DEFAULT_PRODUCTS_WRAPPER_SELECTOR = '.template-collection__products';
const DEFAULT_PRODUCTS_LIST_SELECTOR = '.template-collection__products__list';
const DEFAULT_PRODUCT_ITEM_SELECTOR = '.template-collection__products__item';
const PRODUCT_CARD_SKU_SELECTOR = '[data-dy-sku]';
const PRODUCT_CARD_ITEM_ID_SELECTOR = '[data-dy-item-id]';
const SORTING_PENDING_CLASS =
  'template-collection__products--dy-sorting-pending';
const BODY_SORTING_PENDING_CLASS = 'dy-sorting-optimizer-pending';
const DY_CHOOSE_TIMEOUT = 3000;

class SortingOptimizer {
  constructor(options = {}) {
    this.options = options;
    this.requestId = 0;
    this.run = this.run.bind(this);

    this.init();
  }

  init() {
    if (!this.getProductsWrapper()) {
      return;
    }

    this.registerCollectionCallbacks();
    this.run();

    // Preview helper for manual reruns:
    // window.dySortingOptimizerRun(true)
    window.dySortingOptimizerRun = this.run;
  }

  getProductsWrapper() {
    return document.querySelector(
      this.options.productsWrapperSelector || DEFAULT_PRODUCTS_WRAPPER_SELECTOR
    );
  }

  getProductsList() {
    const productsWrapper = this.getProductsWrapper();

    return productsWrapper
      ? productsWrapper.querySelector(
          this.options.productsListSelector || DEFAULT_PRODUCTS_LIST_SELECTOR
        )
      : null;
  }

  getConfig() {
    const productsWrapper = this.getProductsWrapper();
    const selectorName =
      this.options.selectorName ||
      productsWrapper?.dataset?.dySortingSelector ||
      '';
    const settingEnabled = productsWrapper?.dataset?.dySortingEnabled === 'true';
    const collectionEligible =
      productsWrapper?.dataset?.dySortingTestEnabled === 'true';
    const enabled = collectionEligible && settingEnabled;

    return {
      enabled,
      selectorName,
      settingEnabled,
      collectionEligible,
    };
  }

  registerCollectionCallbacks() {
    window.callbacks_filters_before_render_products =
      window.callbacks_filters_before_render_products || [];
    window.callbacks_filters_after_render_products =
      window.callbacks_filters_after_render_products || [];
    window.callbacks_pagination_after_change =
      window.callbacks_pagination_after_change || [];

    window.callbacks_filters_before_render_products.push(() =>
      this.setSortingPending(true)
    );
    window.callbacks_filters_after_render_products.push(this.run);
    window.callbacks_pagination_after_change.push(this.run);
  }

  getSelectedSortBy() {
    const sortByFromUrl = new URLSearchParams(window.location.search).get(
      'sort_by'
    );

    if (sortByFromUrl) {
      return sortByFromUrl;
    }

    return document.querySelector('#SortBy')?.value || '';
  }

  getSkipReason() {
    const { enabled, selectorName } = this.getConfig();

    if (!selectorName) {
      return 'missing DY Sorting Optimizer API selector';
    }

    if (!enabled) {
      return 'disabled';
    }

    if (!this.getProductsList()) {
      return 'products list not found';
    }

    return '';
  }

  shouldRun() {
    return !this.getSkipReason();
  }

  setSortingPending(isPending) {
    const productsWrapper = this.getProductsWrapper();

    if (!productsWrapper) {
      document.body.classList.remove(BODY_SORTING_PENDING_CLASS);
      return;
    }

    if (!this.getConfig().enabled) {
      productsWrapper.classList.remove(SORTING_PENDING_CLASS);
      document.body.classList.remove(BODY_SORTING_PENDING_CLASS);
      return;
    }

    productsWrapper.classList.toggle(SORTING_PENDING_CLASS, isPending);
    document.body.classList.toggle(BODY_SORTING_PENDING_CLASS, isPending);
  }

  getProductItems() {
    const productsList = this.getProductsList();

    if (!productsList) {
      return [];
    }

    return [
      ...productsList.querySelectorAll(
        this.options.productItemSelector || DEFAULT_PRODUCT_ITEM_SELECTOR
      ),
    ]
      .map((item) => ({
        item,
        itemId: this.getItemId(item),
      }))
      .filter(({ itemId }) => itemId);
  }

  getItemId(item) {
    const productCard = item.querySelector(PRODUCT_CARD_ITEM_ID_SELECTOR);
    const itemId =
      productCard?.dataset?.dyItemId ||
      item.dataset.productId ||
      item.querySelector(PRODUCT_CARD_SKU_SELECTOR)?.dataset?.dySku;

    return itemId ? `${itemId}`.trim() : '';
  }

  getListedItems(items) {
    const uniqueItemIds = [];
    const seenItemIds = new Set();

    items.forEach(({ itemId }) => {
      if (!seenItemIds.has(itemId)) {
        seenItemIds.add(itemId);
        uniqueItemIds.push(itemId);
      }
    });

    return uniqueItemIds.slice(0, MAX_LISTED_ITEMS);
  }

  chooseWithTimeout(options) {
    return Promise.race([
      dyChoose(options),
      new Promise((resolve) => {
        setTimeout(() => resolve(null), DY_CHOOSE_TIMEOUT);
      }),
    ]);
  }

  async run(force = false) {
    const forceRun = force === true || force?.force === true;

    try {
      this.setSortingPending(true);

      const skipReason = this.getSkipReason();

      if (skipReason) {
        return false;
      }

      const productsList = this.getProductsList();
      const productItems = this.getProductItems();
      const listedItems = this.getListedItems(productItems);
      const { selectorName } = this.getConfig();
      const selectedSortBy = this.getSelectedSortBy();
      const requestKey = `${selectorName}:${selectedSortBy}:${listedItems.join(
        '|'
      )}`;

      if (!productsList || !listedItems.length) {
        return false;
      }

      if (
        !forceRun &&
        productsList.dataset.dySortingRequestKey === requestKey
      ) {
        return false;
      }

      productsList.dataset.dySortingRequestKey = requestKey;
      this.requestId += 1;

      const currentRequestId = this.requestId;
      const pageContext = getDefaultPageContext('CATEGORY');

      const dyData = await this.chooseWithTimeout({
        pageContext,
        selectorNames: [selectorName],
        listedItems,
      });

      if (currentRequestId !== this.requestId) {
        return false;
      }

      const sortingData = this.getSortingData(dyData, selectorName);

      if (!sortingData?.slots?.length) {
        return false;
      }

      return this.applySorting({
        productsList,
        productItems,
        sortingData,
      });
    } finally {
      this.setSortingPending(false);
    }
  }

  getSortingData(dyData, selectorName) {
    const choices = Array.isArray(dyData?.choices) ? dyData.choices : [];
    const sortingChoices = choices.filter(
      (choice) =>
        choice?.name === selectorName ||
        `${choice?.type || ''}`.includes('SORT')
    );
    const choicesToCheck = sortingChoices.length ? sortingChoices : choices;

    for (let choiceIndex = 0; choiceIndex < choicesToCheck.length; choiceIndex += 1) {
      const choice = choicesToCheck[choiceIndex];
      const variations = Array.isArray(choice?.variations)
        ? choice.variations
        : [];

      for (
        let variationIndex = 0;
        variationIndex < variations.length;
        variationIndex += 1
      ) {
        const variation = variations[variationIndex];
        const slots = variation?.payload?.data?.slots;

        if (Array.isArray(slots) && slots.length) {
          return {
            choice,
            variation,
            variationIds: variations.map((item) => item.id),
            slots,
          };
        }
      }
    }

    return null;
  }

  applySorting({ productsList, productItems, sortingData }) {
    const itemQueuesById = productItems.reduce((itemsById, productItem) => {
      const itemQueue = itemsById.get(productItem.itemId) || [];

      itemQueue.push(productItem.item);
      itemsById.set(productItem.itemId, itemQueue);

      return itemsById;
    }, new Map());
    const usedItems = new Set();
    const fragment = document.createDocumentFragment();

    sortingData.slots.forEach((slot) => {
      const itemId = slot?.sku ? `${slot.sku}`.trim() : '';
      const itemQueue = itemId ? itemQueuesById.get(itemId) : null;
      const item = itemQueue?.shift();

      if (item) {
        this.setSlotData({
          item,
          slot,
          decisionId: sortingData.choice?.decisionId || '',
          variationIds: sortingData.variationIds,
        });
        fragment.appendChild(item);
        usedItems.add(item);
      }
    });

    if (!usedItems.size) {
      return false;
    }

    productItems.forEach(({ item }) => {
      if (!usedItems.has(item)) {
        fragment.appendChild(item);
      }
    });

    productsList.appendChild(fragment);
    productsList.dataset.dySortingApplied = 'true';

    return true;
  }

  setSlotData({ item, slot, decisionId, variationIds }) {
    const slotId = slot?.slotId || '';
    const productCard = item.querySelector(PRODUCT_CARD_SKU_SELECTOR);
    const productLink =
      item.querySelector('.product-card__link') || item.querySelector('a');

    [productCard, productLink].forEach((element) => {
      if (element) {
        element.dataset.dySlotId = slotId;
        element.dataset.dyDecisionId = decisionId;
      }
    });

    this.bindSlotClick({
      link: productLink,
      variationIds,
    });
  }

  bindSlotClick({ link, variationIds }) {
    if (!link || link.dataset.dySortingClickBound === 'true') {
      return;
    }

    const eventName = !mobile ? 'click' : 'mousedown';

    link.dataset.dySortingClickBound = 'true';
    link.addEventListener(eventName, () => {
      const slotId = link.dataset.dySlotId;
      const decisionId = link.dataset.dyDecisionId;

      if (!slotId || !decisionId) {
        return;
      }

      DynamicYield.reportUserEngagement({
        type: 'SLOT_CLICK',
        decisionId,
        slotId,
        variationsId: variationIds || [],
        apiKey: dyApiConfig.key,
      });
    });
  }
}

export default SortingOptimizer;
